//******************************************************************************************************************** */

import {
  Coins,
  MousePointerClick,
  Play,
  Trophy,
  RotateCcw,
} from "lucide-react";
import { FaArrowRightLong } from "react-icons/fa6";
import { useShowTutorial } from "../Context/modalContex";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

function HowToPlay() {
  const navigate = useNavigate();
  const { showTutorial, setShowTutorial } = useShowTutorial();
    const { user } = useSelector((state) => state.auth);

  const onHandleGuide = () => {
    navigate("/");
    setShowTutorial(true);
  };

  const guides = [
    {
      icon: Coins,
      title: "1. Choose an Amount",
      description:
        "Start by entering or selecting the amount you'd like to stake. This is the amount you'll be playing with for this round.",
    },
    {
      icon: MousePointerClick,
      title: "2. Pick a Side",
      description:
        "Next, choose your prediction — will it be Heads or Tails? Trust your gut or go with your lucky side!",
    },
    {
      icon: Play,
      title: "3. Start the Game",
      description:
        "Once you've made your pick, hit Play and watch as the coin spins in the arena.",
    },
    {
      icon: Trophy,
      title: "4. Wait for the Result",
      description: (
        <>
          If the coin lands on your chosen side, you win instantly and your
          payout will be credited right away! <br /> If not, don’t worry — you
          can try again and flip your luck next round.
        </>
      ),
    },
    {
      icon: RotateCcw,
      title: "5. Play Again Anytime",
      description:
        "Ready for another round? Just pick your amount, choose a side, and go again. Every flip is a fresh chance to win!",
    },
  ];

  return (
    <div className="w-full bg-[#020916] md:py-12 text-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        {/* Title */}
        <h1 className="text-3xl sm:text-4xl md:text-5xl text-center font-bold tracking-wider mb-6 md:mb-8 bg-gradient-to-r from-emerald-400 via-green-500 to-blue-500 bg-clip-text text-transparent drop-shadow-[0_0_10px_rgba(0,255,136,0.3)]">
          How to Play
        </h1>

        {/* Steps */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-7">
          {guides.map((guide, index) => {
            const Icon = guide.icon;
            const isLastOdd =
              guides.length % 2 !== 0 && index === guides.length - 1;

            return (
              <div
                key={index}
                className={`p-5 md:p-6 rounded-2xl bg-[#0b1224] border border-[#0f1c36]  transition-all ${
                  isLastOdd ? "md:col-span-2 md:max-w-xl md:mx-auto" : ""
                }`}
              >
                <div className="flex items-center gap-3 justify-center mb-3">
                  <Icon className="w-6 h-6 sm:w-7 sm:h-7 text-emerald-400" />
                  <h2 className="text-lg sm:text-xl font-semibold bg-gradient-to-bl from-emerald-400 to-blue-500 bg-clip-text text-transparent tracking-wide">
                    {guide.title}
                  </h2>
                </div>
                <p className="text-gray-300 text-base sm:text-lg text-center leading-relaxed">
                  {guide.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>
{user &&
      <div className="flex justify-center mt-6">
        <button
          onClick={onHandleGuide}
          className="flex items-center gap-2 px-6 py-3 
        bg-gradient-to-r from-[#0B192F] via-[#0E2245] to-[#101B33]
        border border-[#00e0ff]/40 text-[#00e0ff]
        font-semibold text-lg rounded-xl
        hover:border-[#00e0ff] hover:text-white
        hover:shadow-[0_0_15px_rgba(0,224,255,0.4)]
        transition-all duration-300 group"
        >
          Game Guide
          <span className="text-[#00e0ff] transition-transform duration-300 group-hover:translate-x-1">
            <FaArrowRightLong className="w-5 h-5" />
          </span>
        </button>
      </div>
}
      {/* Winning Amount Section */}
      <div
        className="mt-10 p-6 rounded-2xl 
        bg-[rgba(2,18,40,0.6)] 
        backdrop-blur-xl 
        border border-[rgba(0,255,136,0.25)] 
        shadow-[0_-10px_10px_rgba(0,255,136,0.15)] 
        relative overflow-hidden"
      >
        {/* Glowing layer */}
        <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-[rgba(0,255,136,0.15)] via-[rgba(0,180,255,0.1)] to-[rgba(0,100,255,0.2)] blur-xl opacity-40"></div>

        {/* Main content */}
        <div className="relative z-10 text-center">
          <h2
            className="text-2xl font-bold text-emerald-400 mb-2 
            drop-shadow-[0_0_8px_rgba(0,255,136,0.4)]
            tracking-wide"
          >
            Winning Amounts 💰
          </h2>

          <p className="text-gray-200 leading-relaxed">
            The{" "}
            <span className="text-emerald-300 font-semibold">
              minimum winning amount
            </span>{" "}
            is
            <span className="text-blue-400 font-semibold"> 1.8× your bet</span>,
            but lucky players can win more than{" "}
            <span className="text-emerald-400 font-semibold">10×</span> their
            original stake — depending on the outcome.
          </p>
        </div>

        {/* Animated shimmer */}
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1/2 h-[2px] bg-gradient-to-r from-transparent via-emerald-400 to-transparent animate-pulse opacity-70"></div>
      </div>
    </div>
  );
}

export default HowToPlay;

//******************************************************************************************************************** */

// import {
//   Coins,
//   MousePointerClick,
//   Play,
//   Trophy,
//   RotateCcw,
// } from "lucide-react";

// function HowToPlay() {
//   const guides = [
//     {
//       icon: Coins,
//       title: "1.Choose an amount",
//       description:
//         "Start by entering or selecting the amount you'd like to stake. This is the amount you'll be playing with for this round.",
//     },
//     {
//       icon: MousePointerClick,
//       title: "2.Pick a Side",
//       description:
//         "Next, choose your prediction — will it be Heads or Tails? Trust your gut or go with your lucky side!",
//     },
//     {
//       icon: Play,
//       title: "3.Start the Game",
//       description:
//         "Once you've made your pick, hit Play and watch as the coin spins.",
//     },
//     {
//       icon: Trophy,
//       title: "4.Wait for the Result",
//       description: (
//         <>
//           If the coin lands on your chosen side, you win instantly and your
//           payout will be credited right away! <br /> If not, don't worry — you
//           can try again and maybe luck will flip in your favor next time.
//         </>
//       ),
//     },
//     {
//       icon: RotateCcw,
//       title: "5.Play Again Anytime",
//       description:
//         "Ready for another round? Just pick your amount, choose a side, and go again. Every flip is a fresh chance to win!",
//     },
//   ];

//   return (
//     <div className="w-full bg-[#071129] md:py-12">
//       <div className="max-w-4xl mx-auto px-4 sm:px-6">
//         <h1 className="text-3xl sm:text-4xl md:text-5xl text-center font-bold tracking-wider mb-6 md:mb-8 text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-600">
//           How to Play
//         </h1>
//         <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
//           {guides.map((guide, index) => {
//             const Icon = guide.icon;
//             const isLastOdd =
//               guides.length % 2 !== 0 && index === guides.length - 1;
//             return (
//               <div
//                 key={index}
//                 className={`p-4 sm:p-5 md:p-6 bg-[#111C32] border border-[#1E2A44] rounded-xl shadow-[6px_6px_4px_rgba(0,255,255,0.1)] transition transform ${
//                   isLastOdd ? "md:col-span-2 md:max-w-xl md:mx-auto" : ""
//                 }`}
//               >
//                 <div className="flex items-center gap-3 justify-center mb-2">
//                   <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-cyan-500 flex-shrink-0" />
//                   <h2 className="text-lg sm:text-xl font-semibold bg-gradient-to-bl from-green-400 to-blue-500 bg-clip-text text-transparent">
//                     {guide.title}
//                   </h2>
//                 </div>
//                 <p className="text-gray-300 text-base sm:text-lg text-center leading-relaxed">
//                   {guide.description}
//                 </p>
//               </div>
//             );
//           })}
//         </div>
//       </div>

//       {/** */}

//       <div
//         className="mt-8 p-6 rounded-2xl
//   bg-[rgba(15,47,112,0.25)]
//   backdrop-blur-xl
//   border border-[rgba(0,224,255,0.35)]
//   shadow-[0_0_25px_rgba(0,224,255,0.15)]
//   relative overflow-hidden"
//       >
//         {/* Subtle glowing border effect */}
//         <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-[rgba(0,255,180,0.15)] via-[rgba(0,224,255,0.1)] to-[rgba(0,100,255,0.2)] blur-xl opacity-40"></div>

//         {/* Main content */}
//         <div className="relative z-10 text-center">
//           <h2
//             className="text-2xl font-bold text-[#7CFFB2] mb-2
//       drop-shadow-[0_0_8px_rgba(0,255,180,0.4)]
//       tracking-wide"
//           >
//             Winning Amounts 💰
//           </h2>

//           <p className="text-gray-200 leading-relaxed">
//             The{" "}
//             <span className="text-[#8AF5B3] font-semibold">
//               minimum winning amount
//             </span>{" "}
//             is
//             <span className="text-[#00e0ff] font-semibold"> 1.8× your bet</span>
//             , but lucky players can win up to{" "}
//             <span className="text-[#00ffbf] font-semibold">8×</span> their
//             original stake, or even{" "}
//             <span className="text-[#00bfff] font-semibold">higher</span> in some
//             rounds — depending on the game’s outcome.
//           </p>
//         </div>

//         {/* Animated shimmer line accent */}
//         <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1/2 h-[2px] bg-gradient-to-r from-transparent via-[#00e0ff] to-transparent animate-pulse opacity-70"></div>
//       </div>

//       {/********* */}
//     </div>
//   );
// }

// export default HowToPlay;

/******************************************************************************************************** */

// import {
//     Coins,
//     MousePointerClick,
//     Play,
//     Trophy,
//     RotateCcw,
//   } from "lucide-react";

//   function HowToPlay() {
//     const guides = [
//       {
//         icon: Coins,
//         title: "1.Choose an amount",
//         description:
//           "Start by entering or selecting the amount you'd like to stake. This is the amount you'll be playing with for this round.",
//       },
//       {
//         icon: MousePointerClick,
//         title: "2.Pick a Side",
//         description:
//           "Next, choose your prediction — will it be Heads or Tails? Trust your gut or go with your lucky side!",
//       },
//       {
//         icon: Play,
//         title: "3.Start the Game",
//         description:
//           "Once you've made your pick, hit Play and watch as the coin spins.",
//       },
//       {
//         icon: Trophy,
//         title: "4.Wait for the Result",
//         description: (
//           <>
//             If the coin lands on your chosen side, you win instantly and your
//             payout will be credited right away! <br /> If not, don't worry — you
//             can try again and maybe luck will flip in your favor next time.
//           </>
//         ),
//       },
//       {
//         icon: RotateCcw,
//         title: "5.Play Again Anytime",
//         description:
//           "Ready for another round? Just pick your amount, choose a side, and go again. Every flip is a fresh chance to win!",
//       },
//     ];
//     return (
//       <div className="w-full bg-[#071129] py-8 md:py-12">
//         <div className="max-w-4xl mx-auto px-4 sm:px-6">
//           <h1 className="text-3xl sm:text-4xl md:text-5xl text-center font-bold tracking-wider mb-6 md:mb-8 text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-600">
//             How to Play
//           </h1>
//           <div className="space-y-4">
//             {guides.map((guide, index) => {
//               const Icon = guide.icon;
//               return (
//                 <div
//                   key={index}
//                   className="p-4 sm:p-5 md:p-6 bg-[#111C32] border border-[#1E2A44] rounded-xl shadow-[6px_6px_4px_rgba(0,255,255,0.1)] transition transform"
//                 >
//                   <div className="flex items-center gap-3 justify-center mb-2">
//                     <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-cyan-500 flex-shrink-0" />
//                     <h2 className="text-lg sm:text-xl font-semibold bg-gradient-to-bl from-green-400 to-blue-500 bg-clip-text text-transparent">
//                       {guide.title}
//                     </h2>
//                   </div>
//                   <p className="text-gray-300 text-base sm:text-lg text-center leading-relaxed">
//                     {guide.description}
//                   </p>
//                 </div>
//               );
//             })}
//           </div>
//         </div>
//       </div>
//     );
//   }

//   export default HowToPlay;
/************************************************************************************************************************ */

// import {
//   Coins,
//   MousePointerClick,
//   Play,
//   Trophy,
//   RotateCcw,
// } from "lucide-react";

// function HowToPlay() {
//   const guides = [
//     {
//       icon: Coins,
//       title: "1.Choose an amount",
//       description:
//         "Start by entering or selecting the amount you'd like to stake. This is the amount you'll be playing with for this round.",
//     },
//     {
//       icon: MousePointerClick,
//       title: "2.Pick a Side",
//       description:
//         "Next, choose your prediction — will it be Heads or Tails? Trust your gut or go with your lucky side!",
//     },
//     {
//       icon: Play,
//       title: "3.Start the Game",
//       description:
//         "Once you've made your pick, hit Play and watch as the coin spins.",
//     },
//     {
//       icon: Trophy,
//       title: "4.Wait for the Result",
//       description: (
//         <>
//           If the coin lands on your chosen side, you win instantly and your
//           payout will be credited right away! <br /> If not, don't worry — you
//           can try again and maybe luck will flip in your favor next time.
//         </>
//       ),
//     },
//     {
//       icon: RotateCcw,
//       title: "5.Play Again Anytime",
//       description:
//         "Ready for another round? Just pick your amount, choose a side, and go again. Every flip is a fresh chance to win!",
//     },
//   ];
//   return (
//     <div className="w-full relative bg-[#071129] ">
//       <h1 className=" text-center font-bold tracking-wider pb-3 absolute -top-10 right-24 md:relative text-4xl md:text-5xl  text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-600 mb-3">
//         How to Play
//       </h1>
//       <div className="space-y-4 px-6 pt-7">
//         {guides.map((guide, index) => {
//           const Icon = guide.icon;
//           return (
//             <div
//               key={index}
//               className=" p-4 bg-[#111C32] border border-[#1E2A44] rounded-xl shadow-[6px_6px_4px_rgba(0,255,255,0.1)] transition transform "
//             >
//               <div className="flex items-center gap-3 justify-center">
//                 <Icon className="w-6 h-6 text-cyan-500" />
//                 <h2
//                   className="text-xl text-center font-semibold bg-gradient-to-bl from-green-400 to-blue-500
//           bg-clip-text text-transparent"
//                 >
//                   {guide.title}
//                 </h2>
//               </div>
//               <p
//                 className="
//           mt-1

//           text-center text-lg
//         "
//               >
//                 {guide.description}
//               </p>
//             </div>
//           );
//         })}
//       </div>
//     </div>
//   );
// }

// export default HowToPlay;
/************og_code*********************************************************************************** */

// function HowToPlay() {
//   const guides = [
//     {
//       title: "1.Choose an amount",
//       description:
//         "Start by entering or selecting the amount you’d like to stake. This is the amount you’ll be playing with for this round.",
//     },
//     {
//       title: "2.Pick a Side",
//       description:
//         "Next, choose your prediction — will it be Heads or Tails? Trust your gut or go with your lucky side!",
//     },
//     {
//       title: "3.Start the Game",
//       description:
//         "Once you’ve made your pick, hit Play and watch as the coin spins.",
//     },
//     {
//       title: "4.Wait for the Result",
//       description: (
//         <>
//           If the coin lands on your chosen side, you win instantly and your
//           payout will be credited right away! <br /> If not, don’t worry — you
//           can try again and maybe luck will flip in your favor next time.
//         </>
//       ),
//     },
//     {
//       title: "5.Play Again Anytime",
//       description:
//         "Ready for another round? Just pick your amount, choose a side, and go again. Every flip is a fresh chance to win!",
//     },
//   ];
//   return (
//     <div className="w-full relative">
//       <h1 className="text-2xl text-center font-bold tracking-wider pb-3 absolute top-[-40px] right-32 ">
//         How to Play
//       </h1>
//       <div className="space-y-4 px-6">
//         {guides.map((guide, index) => (
//           <div
//             key={index}
//             className=" p-4 bg-[#111C32] border border-[#1E2A44] rounded-xl shadow-[6px_6px_4px_rgba(0,255,255,0.1)] transition transform hover:scale-[1.02] hover:shadow-[0_0_15px_rgba(0,255,255,0.2)]"
//           >
//             <h2 className="text-xl text-center font-semibold text-cyan-500 ">
//               {guide.title}
//             </h2>
//             <p
//               className="
//           mt-1
//           bg-gradient-to-bl from-green-400 to-blue-500
//           bg-clip-text text-transparent
//           text-center text-lg
//         "
//             >
//               {guide.description}
//             </p>
//           </div>
//         ))}
//       </div>
//     </div>
//   );
// }

// export default HowToPlay;
